import * as React from 'react';
import EditorImg from 'lowcode/components/EditorImg';
import EfLink from '@elem/ui/ef-link';
import { pageId } from './conf';
import styled from './card.module.scss';
import getLocal from '@/app/utils/getLocal';
import { useLang } from '@/app/utils/useLang';
import { getIsLogin } from '@/utils/cookie';
import { useGlobal } from '@/utils/useGlobal';
import gtm from '@/utils/gtm';

import type { IProductItem } from '@/components/EcocreditsMonthlyMadness/type';
import { signupCookie } from '@/utils/cookies/signupCookie';

export type IProps = IProductItem & {
    link_buy: string;
    skuTitle: string;
    desc: string;
    img: string;
    shopifyHanle: string;
    shopifyTitle: string;
};

const { useState, useEffect, memo } = React;

const addReturnUrl = () => {
    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    url.hash = pageId;
    return url;
};

const ProductCard: React.FC<{ item: IProps }> = ({ item }) => {
    const { useRouter } = useGlobal();
    const router = useRouter();
    const { locale } = getLocal();
    const [isLogin, setIsLogin] = useState(false);

    const { t } = useLang('membersday.product');

    const hanleLearnMore = (title: string) => {
        try {
            gtm.push({
                event: 'uaEvent',
                event_name: 'buy',
                product_name: title,
                position: 'Best Seller_learn more',
            });
        } catch (err) {
            console.log(err);
        }
    };

    const handleBuyNow = React.useCallback(
        (link: string, title: string) => {
            if (!isLogin) {
                try {
                    gtm.push({
                        event: 'uaEvent',
                        event_name: 'activity_join',
                        button_name: `${t('buy_now')}_${title}`,
                    });
                } catch (e) {
                    console.error(e);
                }

                const newUrl = addReturnUrl();
                signupCookie?.({
                    sector_name: item?.shopifyTitle,
                    entry_position: 4,
                });
                router.push(`/login?redirect=${encodeURIComponent(newUrl.href)}`);
            } else if (link) {
                window.open(link, '_blank');

                try {
                    gtm.push({
                        event: 'uaEvent',
                        event_name: 'checkout',
                        button_name: `${title}_${t('buy_now')}`,
                    });
                } catch (err) {
                    console.log(err);
                }
            }
        },
        [isLogin, router, t],
    );

    useEffect(() => {
        setIsLogin(getIsLogin());
    }, []);

    return (
        <div className={styled.ProductCard}>
            <div className="item_card" key={item?.productId}>
                <div className="card_img">
                    <EditorImg className="img" pc={item?.img} h5={item?.img} />
                </div>
                {item?.discount && <div className="card_discount">{locale !== 'fr' ? `${item?.discount} ${t('off')}` : `${t('off')} ${item?.discount}`}</div>}

                <div className="card_text">
                    <div className="card_text_wrapper">
                        <div className="title">{item?.shopifyTitle}</div>
                        <div className="subtitle1">{item?.desc}</div>
                    </div>
                    <div className="context_price">
                        <div className="price_1">{item?.price}</div>
                        {item?.price !== item?.compareAtPrice && item?.compareAtPrice && (
                            <div className="price_2">
                                {item?.compareAtPrice}
                                {locale === 'jp' ? '(税込)' : ''}
                            </div>
                        )}
                    </div>
                    <div className="button_action">
                        <EfLink
                            className="card-btn btn-more"
                            href={item?.shopifyHanle || '#'}
                            onClick={() => {
                                hanleLearnMore(item?.shopifyTitle);
                            }}
                        >
                            {t('learn_more')}
                        </EfLink>
                        {typeof item?.inventoryQuantity === 'number' && item?.inventoryQuantity > 0 && (
                            <div className="card-btn btn-buy" onClick={() => handleBuyNow(item?.link_buy, item?.shopifyTitle)}>
                                {t('buy_now')}
                            </div>
                        )}
                        {typeof item?.inventoryQuantity === 'number' && item?.inventoryQuantity <= 0 && (
                            <button disabled className="card-btn btn-disable">
                                {t('card_btn_sold_out')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(ProductCard);
